import { supabase } from './client';

// Makes sure the Realtime socket carries the user's JWT *before* any channel joins.
//
// Why this exists (measured against production, 2026-07-26):
// RealtimeChannel.subscribe() reads socket.accessTokenValue SYNCHRONOUSLY and only
// then puts it into the phx_join payload:
//
//   if (this.socket.accessTokenValue) {
//     accessTokenPayload.access_token = this.socket.accessTokenValue;
//   }
//   ... this.updateJoinPayload({ config, ...accessTokenPayload });
//
// (node_modules/@supabase/realtime-js/dist/main/RealtimeChannel.js, subscribe())
//
// subscribe() calls socket.connect() first, which kicks off _setAuthSafely('connect')
// — but that is async (it awaits auth.getSession()). So on a cold page load the join
// goes out with accessTokenValue still null, i.e. authorized as `anon`.
//
// That is not a loud failure. The server answers phx_reply "ok" AND
// "Subscribed to PostgreSQL" either way. But RLS is then evaluated as anon, and for
// every table we subscribe to (auth.uid() = user_id) anon sees nothing, so walrus
// silently delivers zero rows. Verified with a controlled A/B of two raw websockets
// with identical postgres_changes params: without access_token 0 events, with
// access_token 1 event, for the same UPDATE.
//
// The library's self-heal does not save us. subscribe()'s join-ok handler calls
// socket.setAuth(), and _performAuth() broadcasts the token to channels — but only
// inside `if (this.accessTokenValue != tokenToSend)`, a GLOBAL dedupe, and the push
// itself additionally requires `channel.joinedOnce && channel._isJoined()`:
//
//   if (this.accessTokenValue != tokenToSend) {
//     this.accessTokenValue = tokenToSend;
//     this.channels.forEach((channel) => {
//       tokenToSend && channel.updateJoinPayload(payload);
//       if (channel.joinedOnce && channel._isJoined()) { channel._push(access_token, ...) }
//     });
//   }
//
// (RealtimeClient.js, _performAuth) So only the FIRST channel whose join-ok lands
// after the token became known triggers the broadcast. Every sibling that was still
// in `joining` at that instant gets updateJoinPayload (which only affects a FUTURE
// rejoin) and no push; when its own join-ok arrives later, accessTokenValue already
// equals tokenToSend and the whole forEach is skipped. Those channels stay
// anon-authorized for the life of the socket.
//
// supabase-js does not rescue it either: _handleTokenChanged only calls
// realtime.setAuth() on SIGNED_OUT. TOKEN_REFRESHED / SIGNED_IN just record the token
// (SupabaseClient.js). So nothing proactively re-authorizes an anon-joined channel.
//
// Conclusion: the token has to be in place BEFORE subscribe() runs. That is what this
// function guarantees, and it is why every postgres_changes subscription in this app
// must go through useRealtimePostgresChanges rather than calling supabase.channel()
// directly (there is a repo-invariant test enforcing that).

// Keyed by user, NOT a bare promise. A bare shared promise reintroduces this very bug
// on a user switch: A signs out, B signs in, and every effect re-runs against the new
// userId — but each one would receive the cached probe for A, so all of B's channels
// join carrying A's JWT. Under auth.uid() = user_id that is silently dead for the life
// of the socket, exactly like the anon join, just rarer and harder to reproduce.
let inflight: { userId: string; promise: Promise<boolean> } | null = null;

/**
 * Resolves once the Realtime socket holds `expectedUserId`'s access token.
 *
 * @param expectedUserId the user the caller intends to subscribe as.
 * @returns true when the socket is authorized as exactly that user; false when there is
 *          no session, or when the session that came back belongs to somebody else.
 *          Callers MUST NOT subscribe on false — a tokenless join is authorized as anon
 *          and silently receives nothing, and a join with the wrong user's token is
 *          just as silently empty.
 *
 * Deliberately does NOT fall back to the publishable key. supabase-js's own
 * _getAccessToken() returns `supabaseKey` when no session exists, which sets
 * accessTokenValue to a non-null anon key — a value that would satisfy a naive
 * "is a token set?" check while still being anon. Hence the explicit session probe.
 *
 * Concurrent callers for the SAME user share one in-flight probe (8 subscribers mount at
 * once on a cold load). The result is intentionally not cached beyond that, so a later
 * call re-reads the session after a token refresh.
 */
export function ensureRealtimeAuth(expectedUserId: string): Promise<boolean> {
  if (inflight && inflight.userId === expectedUserId) return inflight.promise;

  const promise = (async (): Promise<boolean> => {
    const { data } = await supabase.auth.getSession();
    const session = data.session;
    const token = session?.access_token;
    if (!token) return false;
    // The session may have moved on while we were awaiting it. Authorizing the socket
    // as somebody the caller did not ask for is worse than not subscribing: the caller
    // would believe it is subscribed and would receive nothing.
    if (session?.user?.id && session.user.id !== expectedUserId) return false;
    // Sets socket.accessTokenValue so it is visible to the next synchronous
    // subscribe(), and pushes the token to already-joined channels if it changed.
    await supabase.realtime.setAuth(token);
    return true;
  })();

  const entry = { userId: expectedUserId, promise };
  inflight = entry;
  void promise
    .catch(() => false)
    .finally(() => {
      if (inflight === entry) inflight = null;
    });

  return promise;
}

/** Test seam: drops the in-flight probe so each test starts from a clean slate. */
export function __resetRealtimeAuthForTests(): void {
  inflight = null;
}
